import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { uploadContent, resetUpload, clearError } from '../store/contentSlice';
import { loadUser } from '../store/authSlice';
import { TYPES, TYPE_LABELS, SUBGENRES, SUBGENRE_LABELS } from '../data/catalog';
import api from '../api';

const CONTENT_TYPES = TYPES.filter(t => t !== 'all');

const QUALITY_LABELS = { 0: '720p', 1: '1080p', 2: '4K' };

/**
 * Extract video metadata client-side using a <video> element
 */
const extractVideoMetadata = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const durationSeconds = Math.round(video.duration);
      const width = video.videoWidth;
      const height = video.videoHeight;
      let quality = 0;
      if (height >= 2160) quality = 2;
      else if (height >= 1080) quality = 1;
      URL.revokeObjectURL(video.src);
      resolve({ durationSeconds, width, height, quality, sizeBytes: file.size });
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(null);
    };
    video.src = URL.createObjectURL(file);
  });
};

export default function UploadContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { uploading, uploadProgress, uploadStage, error } = useSelector((state) => state.content);

  const [form, setForm] = useState({ title: '', description: '', type: 'movie', genre: '', priceUsdc: '' });
  const [mediaFile, setMediaFile] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [titleError, setTitleError] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null);
  const [fees, setFees] = useState(null);
  const [feeEstimate, setFeeEstimate] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(resetUpload());
      dispatch(clearError());
    };
  }, [dispatch]);

  // Fetch fees on mount
  useEffect(() => {
    api.get('/api/config/fees')
      .then(res => setFees(res.data))
      .catch(() => setFees(null));
  }, []);

  // Calculate fee estimate when metadata or fees change
  useEffect(() => {
    if (!videoMeta || !fees) {
      setFeeEstimate(null);
      return;
    }

    const { durationSeconds, quality, sizeBytes } = videoMeta;
    const durationHours = durationSeconds / 3600;
    const sizeGB = sizeBytes / (1024 * 1024 * 1024);

    // Upload fee: (durationHours * feePerHour * 3), with minimum
    const qualityKey = quality === 2 ? '4K' : quality === 1 ? '1080p' : '720p';
    const feePerHour = parseFloat(fees.uploadFeePerHour[qualityKey]?.formatted || '0');
    const minimum = parseFloat(fees.uploadFeeMinimum?.formatted || '0');
    let uploadFee = durationHours * feePerHour * 3;
    if (uploadFee < minimum) uploadFee = minimum;

    // Monthly storage
    const storageFeePerGB = parseFloat(fees.storageFeePerGB?.formatted || '0');
    const monthlyStorage = sizeGB * storageFeePerGB;

    setFeeEstimate({
      uploadFee: uploadFee.toFixed(2),
      monthlyStorage: monthlyStorage.toFixed(2),
      allZero: uploadFee === 0 && monthlyStorage === 0,
    });
  }, [videoMeta, fees]);

  const handleChange = (e) => {
    if (e.target.name === 'title' && titleError) setTitleError(null);
    if (e.target.name === 'type') {
      setForm({ ...form, type: e.target.value, genre: '' });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const subgenres = (SUBGENRES[form.type] || []).filter(g => g !== 'all');

  const handleFileChange = async (e) => {
    const file = e.target.files[0] || null;
    if (file && !['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'].includes(file.type)) {
      setLocalError('Unsupported format. Use mp4, webm, mov or avi.');
      setMediaFile(null);
      setVideoMeta(null);
      return;
    }
    setLocalError(null);
    setMediaFile(file);

    if (file) {
      const meta = await extractVideoMetadata(file);
      setVideoMeta(meta);
    } else {
      setVideoMeta(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!mediaFile) {
      setLocalError('Select a video file.');
      return;
    }

    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('type', form.type);
    if (form.genre) formData.append('genre', form.genre);
    formData.append('priceUsdc', parseFloat(form.priceUsdc));
    formData.append('media', mediaFile);

    const result = await dispatch(uploadContent(formData));
    if (!result.error) {
      // Refresh user — role may have changed (user → creator on first upload)
      await dispatch(loadUser());
      navigate('/creator');
    } else if (result.payload && result.payload.includes('that name')) {
      setTitleError('That title is already taken. Try changing it a bit.');
      dispatch(clearError());
    }
  };

  return (
    <div className="upload-page">
      <h2>Upload Content</h2>

      {(localError || error) && (
        <div className="error-msg">{localError || error}</div>
      )}

      <form onSubmit={handleSubmit} className="creator-form">
        <div className="upload-title-wrapper">
          <input
            name="title"
            placeholder="Title"
            value={form.title}
            onChange={handleChange}
            required
            disabled={uploading}
            className={titleError ? 'input-error' : ''}
          />
          {titleError && (
            <div className="upload-title-popup">
              <span>{titleError}</span>
              <button type="button" onClick={() => setTitleError(null)}>&times;</button>
            </div>
          )}
        </div>
        <textarea
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={handleChange}
          rows={3}
          disabled={uploading}
        />
        <select name="type" value={form.type} onChange={handleChange} disabled={uploading}>
          {CONTENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        {subgenres.length > 0 && (
          <select name="genre" value={form.genre} onChange={handleChange} disabled={uploading}>
            <option value="">Subcategory</option>
            {subgenres.map((g) => <option key={g} value={g}>{SUBGENRE_LABELS[g] || g}</option>)}
          </select>
        )}
        <div className="file-upload">
          <label className="btn upload-file-btn">
            {mediaFile ? mediaFile.name : 'Select video'}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
          {mediaFile && <span className="file-upload-info">{(mediaFile.size / (1024 * 1024)).toFixed(1)} MB</span>}
        </div>

        {videoMeta && (
          <>
            <div className="upload-meta-info">
              <span>{Math.floor(videoMeta.durationSeconds / 60)}m {videoMeta.durationSeconds % 60}s</span>
              <span>{videoMeta.width}x{videoMeta.height}</span>
              <span className="upload-quality-badge">{QUALITY_LABELS[videoMeta.quality]}</span>
            </div>
            <div className="upload-preview-hint">
              <p>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                Free preview: first {Math.floor(videoMeta.durationSeconds * 0.33 / 60)} min {Math.round(videoMeta.durationSeconds * 0.33 % 60)} sec of {Math.floor(videoMeta.durationSeconds / 60)} min total. Viewers who want to watch the full content can purchase it using their wallet.
              </p>
              <p>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                90% of each sale goes directly to your wallet. The platform keeps 10%.
              </p>
            </div>
          </>
        )}

        <input
          name="priceUsdc"
          type="number"
          step="0.01"
          min="0.01"
          placeholder="Price (USDC)"
          value={form.priceUsdc}
          onChange={handleChange}
          required
          disabled={uploading}
        />

        {feeEstimate && !feeEstimate.allZero && (
          <div className="upload-fee-estimate">
            <div className="upload-fee-row">
              <span>Upload cost (covers 3 months)</span>
              <strong>${feeEstimate.uploadFee} USDC</strong>
            </div>
            <div className="upload-fee-row">
              <span>Monthly storage</span>
              <strong>${feeEstimate.monthlyStorage} USDC/mo</strong>
            </div>
            <Link to="/fees" className="upload-fee-link">Learn more about fees</Link>
          </div>
        )}

        <button type="submit" className="btn btn-primary upload-submit-btn" disabled={uploading}>
          {uploading ? (
            <>
              <span className="upload-btn-clock">
                <span className="upload-btn-clock-ring" />
                <span className="upload-btn-clock-hand" />
                <span className="upload-btn-clock-dot" />
              </span>
              {uploadStage === 'uploading' && `Uploading... ${uploadProgress}%`}
              {uploadStage === 'processing' && 'Processing...'}
              {uploadStage === 'done' && 'Done!'}
              {!uploadStage && 'Preparing...'}
            </>
          ) : (
            'Upload'
          )}
        </button>
      </form>
    </div>
  );
}
